/**
 * Admin: Trade Pages CMS.
 *
 * Two modes driven by the optional `:slug` URL param:
 *   - List mode      (/admin/trade-pages)            → table of all pages
 *   - Editor mode    (/admin/trade-pages/:slug)      → full edit form
 *
 * Editor uses a textarea-driven markdown body so non-technical admins can
 * format with simple `## headings`, `### subheadings`, `- bullets`,
 * `**bold**`, `*italic*` and `[link text](url)`. A "Preview" toggle next to
 * the textarea renders the same react-markdown shell the public page uses,
 * so admins can sanity-check before saving.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, Edit3, ExternalLink, Eye, EyeOff, Image as ImageIcon, RotateCcw, Save, Trash2, Upload } from 'lucide-react';
import api from '../lib/api';
import HelpCallout from './HelpCallout';

function fmt(iso) {
  if (!iso) return '\u2014';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}

const PREVIEW_COMPONENTS = {
  h2: (props) => <h2 className="font-display text-xl text-charcoal mt-6 mb-2 first:mt-0" {...props} />,
  h3: (props) => <h3 className="font-semibold text-base text-charcoal mt-4 mb-1" {...props} />,
  p: (props) => <p className="mb-3 text-charcoal/80 text-sm leading-relaxed" {...props} />,
  ul: (props) => <ul className="mb-3 ml-5 list-disc text-charcoal/80 text-sm space-y-1" {...props} />,
  li: (props) => <li {...props} />,
  a: (props) => <a className="text-heritage underline" {...props} />,
  strong: (props) => <strong className="text-charcoal" {...props} />,
};

export default function AdminTradePages() {
  const { slug } = useParams();
  if (slug) return <TradePageEditor slug={slug} />;
  return <TradePageList />;
}

const CATEGORY_META = {
  marketing: { label: 'Marketing channels', blurb: 'Reach visitors at scale via the printed Guide and digital partner directory.' },
  awards: { label: 'Tourism Awards', blurb: 'Everything related to the annual Tourism &amp; Hospitality Awards.' },
  support: { label: 'Advice &amp; support', blurb: 'Curated guidance, toolkits and external resources for trade partners.' },
  other: { label: 'Other resources', blurb: '' },
};

function TradePageList() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState('');
  const [reseeding, setReseeding] = useState(false);

  async function load() {
    setErr('');
    try {
      const { data } = await api.get('/admin/trade-pages');
      setItems(data.items || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load trade pages');
    }
  }
  useEffect(() => { load(); }, []);

  async function reseed() {
    if (!window.confirm('Reset ALL trade pages to the original built-in content? Your edits will be lost.')) return;
    setReseeding(true);
    try {
      await api.post('/admin/trade-pages/seed');
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Reseed failed');
    } finally { setReseeding(false); }
  }

  // Group items by category, preserving sort_order within each group.
  const grouped = ['marketing', 'awards', 'support', 'other'].map((cat) => ({
    cat,
    meta: CATEGORY_META[cat],
    pages: items.filter((p) => (p.category || 'other') === cat),
  })).filter((g) => g.pages.length > 0);

  return (
    <div className="space-y-6" data-testid="admin-trade-pages-list">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl mb-1">Trade Pages</h1>
          <p className="text-sm text-charcoal/65">{items.length} page{items.length === 1 ? '' : 's'} — edit the content that signed-in partners (and admins) see at <code>/partners/&lt;slug&gt;</code>.</p>
        </div>
        <button
          type="button"
          onClick={reseed}
          disabled={reseeding}
          data-testid="reseed-trade-pages"
          className="inline-flex items-center gap-1.5 border border-charcoal/15 text-charcoal text-sm rounded-full px-4 py-2 hover:bg-stone disabled:opacity-50"
          title="Restore original built-in content for every page"
        >
          <RotateCcw size={14} /> Restore defaults
        </button>
      </header>

      <HelpCallout>
        Pages are markdown-based. Save here and the change is live on the public site
        within a second — no rebuild needed. Use <strong>## Heading</strong> for sections, <strong>### Sub-heading</strong> for
        sub-sections, <strong>- item</strong> for bullets, <strong>**bold**</strong>, <strong>*italic*</strong> and
        <strong> [link text](https://…)</strong> for links.
      </HelpCallout>

      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{err}</div>}

      <div className="space-y-10" data-testid="trade-pages-grouped">
        {grouped.map((group) => (
          <section key={group.cat} data-testid={`trade-pages-group-${group.cat}`}>
            <header className="mb-4 pb-3 border-b border-charcoal/10">
              <div className="flex items-baseline gap-3">
                <h2 className="font-display text-xl text-charcoal">{group.meta.label}</h2>
                <span className="text-xs text-charcoal/55">{group.pages.length} page{group.pages.length === 1 ? '' : 's'}</span>
              </div>
              {group.meta.blurb && <p className="text-sm text-charcoal/65 mt-1" dangerouslySetInnerHTML={{ __html: group.meta.blurb }} />}
            </header>
            <ul className="space-y-3">
              {group.pages.map((p) => (
                <li key={p.slug} className="bg-white border border-charcoal/10 rounded-2xl p-5" data-testid={`trade-page-row-${p.slug}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold text-charcoal">{p.title}</span>
                        <code className="text-xs bg-stone px-2 py-0.5 rounded text-charcoal/65">/partners/{p.slug}</code>
                        {p.cover_image_url && <span className="text-xs bg-mustard/30 text-charcoal px-2 py-0.5 rounded">cover image</span>}
                      </div>
                      <div className="text-sm text-charcoal/70 max-w-2xl line-clamp-2">{p.intro}</div>
                      <div className="text-xs text-charcoal/55 mt-1">
                        Last updated {fmt(p.updated_at)}{p.updated_by ? ` by ${p.updated_by}` : ''}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <a
                        href={`/partners/${p.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`view-public-${p.slug}`}
                        className="inline-flex items-center gap-1.5 border border-charcoal/15 text-charcoal text-sm rounded-full px-4 py-2 hover:bg-stone"
                      >
                        <ExternalLink size={14} /> View
                      </a>
                      <Link
                        to={`/admin/trade-pages/${p.slug}`}
                        data-testid={`edit-${p.slug}`}
                        className="inline-flex items-center gap-1.5 bg-heritage text-base text-sm font-semibold rounded-full px-4 py-2 hover:bg-charcoal"
                      >
                        <Edit3 size={14} /> Edit
                      </Link>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

function TradePageEditor({ slug }) {
  const navigate = useNavigate();
  const [original, setOriginal] = useState(null);
  const [form, setForm] = useState(null);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/trade-pages/${slug}`);
        if (!cancelled) {
          setOriginal(data);
          setForm({ ...data });
        }
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || 'Failed to load page');
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const dirty = useMemo(() => {
    if (!original || !form) return false;
    return ['title', 'label', 'intro', 'body_md', 'cover_image_url', 'cta_href', 'cta_label', 'contact_email', 'contact_cta']
      .some((k) => (original[k] || '') !== (form[k] || ''));
  }, [original, form]);

  async function save() {
    if (saving || !form) return;
    setSaving(true); setErr(''); setSaved(false);
    try {
      const { data } = await api.put(`/admin/trade-pages/${slug}`, {
        title: form.title,
        label: form.label,
        intro: form.intro,
        body_md: form.body_md,
        cover_image_url: form.cover_image_url || '',
        cta_href: form.cta_href,
        cta_label: form.cta_label,
        contact_email: form.contact_email,
        contact_cta: form.contact_cta,
      });
      setOriginal(data);
      setForm({ ...data });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  }

  function reset() {
    if (!original) return;
    if (dirty && !window.confirm('Discard your unsaved changes?')) return;
    setForm({ ...original });
  }

  if (err && !form) {
    return (
      <div className="text-sm text-red-700">
        {err} — <Link to="/admin/trade-pages" className="underline">back to list</Link>
      </div>
    );
  }
  if (!form) return <div className="text-sm text-charcoal/55">Loading\u2026</div>;

  return (
    <div className="space-y-6" data-testid={`admin-trade-page-editor-${slug}`}>
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <button type="button" onClick={() => { dirty ? (window.confirm('Discard your unsaved changes?') && navigate('/admin/trade-pages')) : navigate('/admin/trade-pages'); }} className="inline-flex items-center gap-1.5 text-sm text-charcoal/65 hover:text-heritage mb-2" data-testid="back-to-trade-pages">
            <ArrowLeft size={14} /> Back to trade pages
          </button>
          <h1 className="font-display text-3xl">{original?.title}</h1>
          <div className="text-xs text-charcoal/55 mt-1">
            <code className="bg-stone px-2 py-0.5 rounded">/partners/{slug}</code>
            {' \u00b7 '}Last saved {fmt(original?.updated_at)}{original?.updated_by ? ` by ${original.updated_by}` : ''}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          {saved && <span className="text-xs text-green-700 font-semibold" data-testid="saved-indicator">Saved \u2713</span>}
          <a href={`/partners/${slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 border border-charcoal/15 text-charcoal text-sm rounded-full px-4 py-2 hover:bg-stone">
            <ExternalLink size={14} /> Open public page
          </a>
          <button type="button" onClick={reset} disabled={!dirty} className="inline-flex items-center gap-1.5 border border-charcoal/15 text-charcoal text-sm rounded-full px-4 py-2 hover:bg-stone disabled:opacity-40" data-testid="reset-form">
            Discard
          </button>
          <button type="button" onClick={save} disabled={!dirty || saving} data-testid="save-trade-page" className="inline-flex items-center gap-1.5 bg-heritage text-base text-sm font-semibold rounded-full px-5 py-2 hover:bg-charcoal disabled:opacity-50">
            <Save size={14} /> {saving ? 'Saving\u2026' : 'Save changes'}
          </button>
        </div>
      </header>

      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{err}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextField label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="field-title" />
        <TextField label="Label (sub-title pill)" value={form.label} onChange={(v) => setForm({ ...form, label: v })} testid="field-label" />
      </div>

      <TextArea
        label="Intro (1–2 sentences in the hero)"
        value={form.intro}
        onChange={(v) => setForm({ ...form, intro: v })}
        rows={3}
        testid="field-intro"
      />

      <CoverImageField
        value={form.cover_image_url || ''}
        onChange={(v) => setForm({ ...form, cover_image_url: v })}
      />

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-xs uppercase tracking-wider text-charcoal/55">Body (Markdown)</label>
          <button type="button" onClick={() => setShowPreview((p) => !p)} className="inline-flex items-center gap-1 text-xs text-heritage hover:underline" data-testid="toggle-preview">
            {showPreview ? <><EyeOff size={12} /> Hide preview</> : <><Eye size={12} /> Show preview</>}
          </button>
        </div>
        <div className={showPreview ? 'grid grid-cols-1 md:grid-cols-2 gap-4' : ''}>
          <textarea
            value={form.body_md}
            onChange={(e) => setForm({ ...form, body_md: e.target.value })}
            rows={28}
            data-testid="field-body_md"
            className="w-full bg-white border border-charcoal/15 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-heritage leading-relaxed"
          />
          {showPreview && (
            <div className="border border-charcoal/15 rounded-lg p-4 bg-stone/30 overflow-y-auto" data-testid="markdown-preview" style={{ maxHeight: '70vh' }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={PREVIEW_COMPONENTS}>
                {form.body_md || ''}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-charcoal/10">
        <TextField label="CTA button URL (optional)" value={form.cta_href || ''} onChange={(v) => setForm({ ...form, cta_href: v })} testid="field-cta_href" placeholder="https://\u2026" />
        <TextField label="CTA button label (optional)" value={form.cta_label || ''} onChange={(v) => setForm({ ...form, cta_label: v })} testid="field-cta_label" placeholder="Start an application" />
        <TextField label="Contact email (optional)" value={form.contact_email || ''} onChange={(v) => setForm({ ...form, contact_email: v })} testid="field-contact_email" />
        <TextField label="Contact CTA text (optional)" value={form.contact_cta || ''} onChange={(v) => setForm({ ...form, contact_cta: v })} testid="field-contact_cta" placeholder="Email us about the awards" />
      </div>
    </div>
  );
}

function CoverImageField({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const inputRef = useRef(null);

  async function upload(file) {
    if (!file) return;
    setUploading(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/media/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.url);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div data-testid="cover-image-field">
      <label className="block text-xs uppercase tracking-wider text-charcoal/55 mb-1">
        Cover image (shown at the top of the public page — optional)
      </label>
      <div className="bg-white border border-charcoal/15 rounded-lg p-4 space-y-3">
        {value ? (
          <div className="relative">
            <img
              src={value}
              alt="Cover preview"
              className="rounded-lg max-h-48 border border-charcoal/10"
              data-testid="cover-image-preview"
            />
            <button
              type="button"
              onClick={() => onChange('')}
              data-testid="cover-image-remove"
              className="absolute top-2 right-2 inline-flex items-center gap-1 bg-white/95 border border-charcoal/15 text-charcoal text-xs rounded-full px-3 py-1.5 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
            >
              <Trash2 size={12} /> Remove
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3 text-charcoal/55 text-sm py-4">
            <ImageIcon size={20} /> No cover image set
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="file"
            accept="image/*"
            ref={inputRef}
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid="cover-image-upload-input"
            className="hidden"
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="cover-image-upload-button"
            className="inline-flex items-center gap-1.5 bg-charcoal text-base text-sm font-semibold rounded-full px-4 py-2 hover:bg-heritage disabled:opacity-50"
          >
            <Upload size={14} /> {uploading ? 'Uploading\u2026' : (value ? 'Replace image' : 'Upload image')}
          </button>
          <span className="text-xs text-charcoal/55">JPG, PNG, WebP or HEIC (iPhone) — up to 25 MB</span>
        </div>
        {err && <div className="text-sm text-red-700" data-testid="cover-image-error">{err}</div>}
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-charcoal/55 mb-1">{label}</label>
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        placeholder={placeholder}
        className="w-full bg-white border border-charcoal/15 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-heritage"
      />
    </div>
  );
}

function TextArea({ label, value, onChange, rows = 4, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-charcoal/55 mb-1">{label}</label>
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        data-testid={testid}
        className="w-full bg-white border border-charcoal/15 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-heritage"
      />
    </div>
  );
}
